import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

export type UserProfile = {
  id: string;
  avatar_url: string | null;
  full_name: string | null;
  updated_at: string;
  user_type: string;
  login_type: string | null;
  email?: string;
  ownerData?: {
    business_type_id: string | null;
    company_name: string | null;
    created_at: string;
    currency: string | null;
    owner_id: string;
    business_types?: {
      name: string;
      description: string | null;
    };
  };
};

export const useUserProfile = () => {
  return useQuery({
    queryKey: ["currentUser"],
    queryFn: async () => {
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      if (userError) {
        console.error("Error fetching user:", userError);
        throw userError;
      }
      if (!user) throw new Error("No user found");
      
      console.log("Current user:", user);
      
      // First get the user profile
      const { data: profile, error: profileError } = await supabase
        .from("profiles")
        .select("*")
        .eq("id", user.id)
        .maybeSingle();
      
      if (profileError) {
        console.error("Error fetching profile:", profileError);
        throw profileError;
      }

      if (!profile) {
        console.error("No profile found for user:", user.id);
        throw new Error("Profile not found");
      }

      console.log("Profile data:", profile);

      if (profile.user_type === "owner") {
        try {
          // Get the default business type for new owners
          const { data: defaultBusinessType, error: businessTypeError } = await supabase
            .from("business_types")
            .select("id, name, description")
            .eq("name", "Commerce général") // You can change this to any default business type
            .maybeSingle();

          if (businessTypeError) {
            console.error("Error fetching default business type:", businessTypeError);
            throw businessTypeError;
          }

          // Then get the owner data with business type info
          const { data: ownerData, error: ownerError } = await supabase
            .from("owner_data")
            .select(`
              *,
              business_types (
                name,
                description
              )
            `)
            .eq("owner_id", user.id)
            .maybeSingle();

          if (ownerError) {
            console.error("Error fetching owner data:", ownerError);
            throw ownerError;
          }

          if (!ownerData) {
            console.log("No owner data found, creating initial record");
            
            // Create owner data with the default business type
            const { data: newOwnerData, error: createError } = await supabase
              .from("owner_data")
              .insert([{ 
                owner_id: user.id,
                business_type_id: defaultBusinessType?.id || null,
              }])
              .select()
              .single();

            if (createError) {
              console.error("Error creating owner data:", createError);
              throw createError;
            }

            return { 
              ...profile, 
              email: user.email,
              ownerData: newOwnerData
            };
          }

          console.log("Owner data:", ownerData);

          return { 
            ...profile, 
            email: user.email,
            ownerData 
          };
        } catch (error) {
          console.error("Error in owner data fetch:", error);
          toast.error("Erreur lors du chargement des données");
          throw error;
        }
      }
      
      return { ...profile, email: user.email };
    },
  });
};